import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AppServices } from 'middleware'
import { ReportingCategory, NewReportingCategory } from './schema'
import { createQueryResponseHandling } from 'utils/errorHandler'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'

export const reportingCategoriesApi = createApi({
    reducerPath: 'reportingCategoriesApi',
    tagTypes: ['reportingCategories'],
    baseQuery: fetchBaseQuery({}),
    endpoints: (builder) => ({}),
})

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) {
        return error.message
    }
    return fallback
}

const extendedApi = reportingCategoriesApi.injectEndpoints({
    endpoints: (builder) => ({
        getReportingCategories: builder.query<ReportingCategory[], string>({
            providesTags: ['reportingCategories'],
            queryFn: async (accountSlug) => {
                const response = await createQueryResponseHandling(() =>
                    AppServices.reportingCategoriesService.getReportingCategories(accountSlug),
                )
                if (response.error) {
                    showErrorMessage(getErrorMessage(response.error, 'Failed to load reporting categories'), 'reporting_categories_load_error')
                }
                return response
            },
        }),
        createReportingCategory: builder.mutation<
            ReportingCategory,
            { accountSlug: string; category: NewReportingCategory }
        >({
            invalidatesTags: ['reportingCategories'],
            queryFn: async ({ accountSlug, category }) => {
                const { articles, ...categoryData } = category
                const articleUuids = articles.map((a) => a.uuid)
                const response = await createQueryResponseHandling(() =>
                    AppServices.reportingCategoriesService.createReportingCategory(accountSlug, categoryData as NewReportingCategory),
                )
                if (response.error) {
                    showErrorMessage(getErrorMessage(response.error, 'Failed to create reporting category'), 'reporting_category_create_error')
                    return response
                }
                if (articleUuids.length > 0) {
                    const assignResponse = await createQueryResponseHandling(() =>
                        AppServices.reportingCategoriesService.assignArticles(accountSlug, response.data.uuid, articleUuids),
                    )
                    if (assignResponse.error) {
                        showErrorMessage(getErrorMessage(assignResponse.error, 'Failed to assign articles to reporting category'), 'reporting_category_assign_error')
                        return response
                    }
                }
                showSuccessMessage('Reporting category created successfully', 'reporting_category_create_success')
                return response
            },
        }),
        updateReportingCategory: builder.mutation<
            ReportingCategory,
            { accountSlug: string; category: ReportingCategory }
        >({
            invalidatesTags: ['reportingCategories'],
            queryFn: async ({ accountSlug, category }) => {
                const { articles, ...categoryData } = category
                const articleUuids = articles.map((a) => a.uuid)
                const response = await createQueryResponseHandling(() =>
                    AppServices.reportingCategoriesService.updateReportingCategory(accountSlug, categoryData as ReportingCategory),
                )
                if (response.error) {
                    showErrorMessage(getErrorMessage(response.error, 'Failed to update reporting category'), 'reporting_category_update_error')
                    return response
                }
                const assignResponse = await createQueryResponseHandling(() =>
                    AppServices.reportingCategoriesService.assignArticles(accountSlug, category.uuid, articleUuids),
                )
                if (assignResponse.error) {
                    showErrorMessage(getErrorMessage(assignResponse.error, 'Failed to assign articles to reporting category'), 'reporting_category_assign_error')
                    return response
                }
                showSuccessMessage('Reporting category updated successfully', 'reporting_category_update_success')
                return response
            },
        }),
        deleteReportingCategory: builder.mutation<void, { accountSlug: string; uuid: string }>({
            invalidatesTags: ['reportingCategories'],
            queryFn: async ({ accountSlug, uuid }) => {
                const response = await createQueryResponseHandling(() =>
                    AppServices.reportingCategoriesService.deleteReportingCategory(accountSlug, uuid),
                )
                if (response.error) {
                    showErrorMessage(getErrorMessage(response.error, 'Failed to delete reporting category'), 'reporting_category_delete_error')
                } else {
                    showSuccessMessage('Reporting category deleted successfully', 'reporting_category_delete_success')
                }
                return response
            },
        }),
    }),
})

export const { reducer, reducerPath } = reportingCategoriesApi
export const {
    useGetReportingCategoriesQuery,
    useCreateReportingCategoryMutation,
    useUpdateReportingCategoryMutation,
    useDeleteReportingCategoryMutation,
} = extendedApi
